/**
 * @CODE:ACC-003-GALLERY-001
 * AccommodationImageGallery Component
 *
 * Image carousel for accommodation detail page:
 * - Embla Carousel for smooth image slideshow
 * - Previous/Next navigation buttons
 * - Pagination indicator (current/total)
 * - "View All Photos" button for full gallery dialog
 * - Keyboard navigation (arrow keys)
 * - Fallback image when no photos available
 * - Image error handling
 */

import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Fade from "embla-carousel-fade";
import {
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Grid3x3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { AccommodationImage } from "../types";

/**
 * Carousel configuration constants
 */
const CAROUSEL_CONFIG = {
  /** Loop back to start when reaching the end */
  LOOP: true,
  /** Fade transition duration in milliseconds */
  FADE_DURATION: 20,
} as const;

/**
 * Dialog configuration constants
 */
const DIALOG_CONFIG = {
  /** Number of columns in mobile view */
  GRID_COLS_MOBILE: "grid-cols-2",
  /** Number of columns in tablet/desktop view */
  GRID_COLS_MD: "md:grid-cols-3",
} as const;

interface AccommodationImageGalleryProps {
  /** Array of accommodation images to display */
  images: AccommodationImage[];
  /** Accommodation name for alt text and dialog title */
  accommodationName: string;
  /** Optional CSS class */
  className?: string;
}

/**
 * Fallback component when no images are available
 * Displays a placeholder with icon and message
 */
function ImageGalleryFallback({
  className,
}: {
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        "relative w-full bg-gray-200 rounded-lg flex items-center justify-center aspect-[16/10]",
        className
      )}
    >
      <div className="text-center text-gray-500">
        <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p className="text-sm">이미지가 없습니다</p>
      </div>
    </div>
  );
}

/**
 * Image carousel component for accommodation details
 * Displays images with navigation, pagination, and full gallery view
 */
export const AccommodationImageGallery = ({
  images,
  accommodationName,
  className,
}: AccommodationImageGalleryProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: CAROUSEL_CONFIG.LOOP,
      duration: CAROUSEL_CONFIG.FADE_DURATION,
    },
    [Fade()]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [imageLoadStates, setImageLoadStates] = useState<boolean[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  // Initialize carousel and set up event listeners
  useEffect(() => {
    if (!emblaApi) return;

    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();

    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, onSelect]);

  // Initialize image load states
  useEffect(() => {
    setImageLoadStates(new Array(images.length).fill(false));
  }, [images.length]);

  // Handle image load
  const handleImageLoad = (index: number) => {
    setImageLoadStates((prev) => {
      const newStates = [...prev];
      newStates[index] = true;
      return newStates;
    });
  };

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );

  // Fallback when no images
  if (!images || images.length === 0) {
    return <ImageGalleryFallback className={className} />;
  }

  return (
    <div className={cn("relative group", className)}>
      {/* Main Carousel */}
      <div
        ref={carouselRef}
        className="relative w-full bg-gray-100 rounded-lg overflow-hidden aspect-[16/10]"
        role="region"
        aria-roledescription="carousel"
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <div className="absolute inset-0 overflow-hidden" ref={emblaRef}>
          <div className="flex h-full">
            {images.map((image, index) => (
              <div
                key={image.image_id || `carousel-image-${index}`}
                className="flex-[0_0_100%] min-w-0 h-full"
              >
                <div className="relative w-full h-full bg-gray-200">
                  {/* iOS Safari fix: Use CSS visibility instead of conditional rendering */}
                  <div
                    className="absolute inset-0 flex items-center justify-center bg-gray-200 animate-pulse"
                    style={{ display: imageLoadStates[index] ? 'none' : 'flex' }}
                  >
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <img
                    src={image.image_url}
                    alt={`${accommodationName} 이미지 ${index + 1}`}
                    className={cn(
                      "w-full h-full object-cover transition-opacity duration-300",
                      imageLoadStates[index] ? "opacity-100" : "opacity-0"
                    )}
                    onLoad={() => handleImageLoad(index)}
                    onError={() => handleImageLoad(index)}
                  />

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Counter */}
        <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm font-medium z-10">
          {selectedIndex + 1} / {images.length}
        </div>

        {/* Navigation Buttons - iOS Safari fix: Use div wrapper instead of Fragment */}
        <div
          className="contents"
          style={{ display: images.length > 1 ? 'contents' : 'none' }}
        >
          <Button
            variant="outline"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 hover:bg-white border-white/50 shadow-lg z-20"
            onClick={scrollPrev}
            aria-label="이전 이미지"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 hover:bg-white border-white/50 shadow-lg z-20"
            onClick={scrollNext}
            aria-label="다음 이미지"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* View All Photos Button */}
      {images.length > 0 && (
        <Button
          variant="outline"
          size="sm"
          className="absolute bottom-4 right-4 bg-white/90 hover:bg-white border-white/50 shadow-lg z-20 gap-2"
          onClick={() => setIsDialogOpen(true)}
          aria-label="모든 사진 보기"
        >
          <Grid3x3 className="h-4 w-4" />
          모든 사진
        </Button>
      )}

      {/* Full Gallery Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          className="max-w-4xl overflow-auto rounded-xl"
          style={{
            maxHeight: 'calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 2rem)',
            maxWidth: 'calc(100vw - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px) - 2rem)',
            marginTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
            marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
            marginLeft: 'calc(env(safe-area-inset-left, 0px) + 1rem)',
            marginRight: 'calc(env(safe-area-inset-right, 0px) + 1rem)'
          }}
        >
          <DialogHeader>
            <DialogTitle>{accommodationName} 사진</DialogTitle>
            <DialogDescription>
              숙박시설의 모든 사진을 확인하세요
            </DialogDescription>
          </DialogHeader>

          {/* Gallery Grid */}
          <div className={cn(DIALOG_CONFIG.GRID_COLS_MOBILE, DIALOG_CONFIG.GRID_COLS_MD, "grid gap-4 mt-4 pb-4")}>
            {images.map((image, index) => (
              <div
                key={image.image_id || `dialog-image-${index}`}
                className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden group cursor-pointer"
              >
                <img
                  src={image.image_url}
                  alt={`${accommodationName} 사진`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
